// eslint-disable-next-line no-restricted-imports
import EsriConfig from '@arcgis/core/config';
import * as React from 'react';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { logMapError } from './helpers/utils';
import { EsriAlternativeBasemapsIds } from './configurations';

/**
 * Component which will set required Esri configuration globally, via the config singleton provided by Esri's SDK.
 */
export function EsriConfiguration({
    children,
}: React.PropsWithChildren<Record<never, never>>): JSX.Element {
    const state = useBootstrapState();
    React.useEffect(() => {
        if (state.esriApiKey) {
            EsriConfig.apiKey = state.esriApiKey;
        }
    }, [state.esriApiKey]);

    React.useEffect(() => {
        // https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#RequestInterceptor
        if (Array.isArray(EsriConfig.request.interceptors)) {
            EsriConfig.request.interceptors.push({
                urls: [
                    // 'dark-gray' and 'imagery' base maps
                    'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/webmaps/arcgis',
                    // alternative dark base map
                    `https://www.arcgis.com/sharing/rest/content/items/${EsriAlternativeBasemapsIds.DARK}/data`,
                    // alternative satellite base map
                    `https://www.arcgis.com/sharing/rest/content/items/${EsriAlternativeBasemapsIds.SATELLITE}/data`,
                    // web portal
                    'https://www.arcgis.com/sharing/rest/content/items/webmapPortalId',
                    // if any basemap or webmap portal requests failed. 'sharing/rest/portals/self' will also fail. But there may be other reasons that cause this request to fail. Monitor both for now.
                    'https://www.arcgis.com/sharing/rest/portals/self',
                ],
                // uncomment for debugging
                // before: (params) => {
                //     console.warn('params', params);
                // },
                error: (error) => {
                    if (error.details?.url?.includes('sharing/rest/portals/self')) {
                        logMapError({
                            source: 'service',
                            event: 'portal_error',
                            error,
                        });
                        return;
                    }

                    logMapError({
                        source: 'service',
                        event: error.message?.includes('Token')
                            ? 'token_invalid'
                            : 'basemap_load_error',
                        error,
                    });
                    return;
                },
            });
        }
    });

    // Disabling rule so that we can use a fragment. If we just return `children` we run into issues when using this component
    // as `ReactNode` isn't assignable to `JSX.Element`.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
}
